// Helpers de garde pour la suppression d’un lieu (éviter données orphelines / mortes).

export type SiteDeleteBlockers = {
  /** Ventes produits rattachées au lieu. */
  productSales: number;
  /** Prestations avec siteId ou consommation stock sur ce lieu. */
  prestations: number;
  /** Produits avec stockBySite[siteId] > 0. */
  productsWithStock: number;
  /** Mouvements de stock historiques. */
  stockMovements: number;
  /** Approvisionnements / transferts (siteId ou fromSiteId). */
  deliveries: number;
  /** Bons d’envoi externes. */
  externalDispatches: number;
  /** Paiements en plusieurs rattachés au lieu. */
  paymentPlans: number;
  /** Sessions d’inventaire physique. */
  inventorySessions: number;
  /** Ajustements d’inventaire. */
  inventoryAdjustments: number;
  /** Dernier point de vente en mode boutique seule. */
  isSolePointOfSale?: boolean;
};

export type SiteDeleteCheckResult = {
  canDelete: boolean;
  reason?: string;
  blockers: SiteDeleteBlockers;
};

const BLOCKER_LABELS: Array<{ key: keyof SiteDeleteBlockers; label: string }> = [
  { key: 'productSales', label: 'vente(s) produit' },
  { key: 'prestations', label: 'prestation(s)' },
  { key: 'productsWithStock', label: 'produit(s) avec stock > 0' },
  { key: 'stockMovements', label: 'mouvement(s) de stock' },
  { key: 'deliveries', label: 'approvisionnement(s) / transfert(s)' },
  { key: 'externalDispatches', label: 'bon(s) externe(s)' },
  { key: 'paymentPlans', label: 'paiement(s) en plusieurs' },
  { key: 'inventorySessions', label: 'inventaire(s) physique(s)' },
  { key: 'inventoryAdjustments', label: 'ajustement(s) d’inventaire' },
];

export const emptySiteDeleteBlockers = (): SiteDeleteBlockers => ({
  productSales: 0,
  prestations: 0,
  productsWithStock: 0,
  stockMovements: 0,
  deliveries: 0,
  externalDispatches: 0,
  paymentPlans: 0,
  inventorySessions: 0,
  inventoryAdjustments: 0,
  isSolePointOfSale: false,
});

/** Construit le motif de refus à partir des compteurs (testable sans Firestore). */
export const buildSiteDeleteBlockReason = (blockers: SiteDeleteBlockers): string | null => {
  if (blockers.isSolePointOfSale) {
    return 'Impossible de supprimer le seul point de vente / boutique de ce business.';
  }

  const parts = BLOCKER_LABELS.filter(({ key }) => {
    const value = blockers[key];
    return typeof value === 'number' && value > 0;
  }).map(({ key, label }) => {
    const count = blockers[key] as number;
    return `${count} ${label}`;
  });

  if (parts.length === 0) return null;

  return (
    'Impossible de supprimer ce lieu : des données y sont encore liées (' +
    parts.join(', ') +
    '). Désactivez-le plutôt, ou transférez / soldez le stock et l’historique avant suppression.'
  );
};

export const evaluateSiteDelete = (blockers: SiteDeleteBlockers): SiteDeleteCheckResult => {
  const reason = buildSiteDeleteBlockReason(blockers);
  return {
    canDelete: !reason,
    reason: reason ?? undefined,
    blockers,
  };
};
